
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter } from "vuex-class"

import Guid from "@/scripts/guid"

import { CaseColumn } from "@/types/caseColumns"
import { Operator } from "@/types/insights"
import { DateRangeOptions, dateRangeLabels } from "@/types/insights/dateRange"
import { DateClauseType } from "@/types/insights/dateClauseType"
import { DynamicValue } from "@/types/dynamic"

import vSelect from "vue-select"

import DataSourceSelect from "@/components/Fields/DataSourceSelect.vue"

import DateTime from "@/components/Fields/DateTime.vue"
import Numeric from "@/components/Fields/Numeric.vue"
import MapSearchBounds from "./MapSearchBounds.vue"

import { SearchClauseParameters, DayOfWeek } from "@/types/view"
import { cloneDeep } from "lodash"
import MonthPicker from "@/components/Controls/MonthPicker.vue"

import moment from "moment"
import { TenantFeatures } from "@/types/tenantFeatures"

const caseStore = namespace("cases")

@Component({
	components: {
		"v-select": vSelect,
		datetime: DateTime,
		numeric: Numeric,
		"map-search": MapSearchBounds,
		"dataSourceSelect": DataSourceSelect,
		"month-picker": MonthPicker
	}
})
export default class DateTimeParameterValue extends Vue {
	@Getter getDarkMode!: boolean;

	@Prop({ default: null })
	field!: CaseColumn;

	@Prop({ default: null })
	value!: DynamicValue | null;

	@Prop({ default: null })
	operator!: Operator | null;

	@Prop({ default: true })
	allowDynamic!: boolean

	@Prop()
	selectedSearchClauseParameters!: SearchClauseParameters

	@Prop({ default: () => Guid.newGuid() })
	clauseIndex!: number | string;

	@Prop({ default: false })
	liveFiltering!: boolean

	@Prop({ type: Boolean })
	utcDates!: boolean

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	private selectedDayOfWeek: string | null = null;
	private selectedWeekNumber: number | null = null;
	private startDate: Date | null = null;
	private endDate: Date | null = null;
	private startMonthRange: Date | null = null;
	private endMonthRange: Date | null = null;
	private selectedDateClauseType: DateClauseType | null = null;

	private dateRangeLabels = dateRangeLabels
	private DateClauseType = DateClauseType

	private selectedItem = null;
	DayOfWeek = DayOfWeek
	TenantFeatures = TenantFeatures

	private get dateValue (): string | null {
		return this.value ? this.value.value : null
	}

	private get dynamicValue (): boolean {
		return this.value ? this.value.dynamic : false
	}

	private get daysOfWeekNames (): string[] {
		return Object.values(DayOfWeek).filter(value => typeof value === "string") as string[]
	}

	private get monthRangeStartDate (): Date {
		return this.selectedSearchClauseParameters!.startDate!
	}

	private get monthRangeEndDate (): Date {
		var dateRange = this.selectedSearchClauseParameters!.endDate ? this.selectedSearchClauseParameters.endDate : moment().endOf("year").toDate()
		return this.selectedSearchClauseParameters!.endDate!
	}

	private get hasWeekStart (): boolean {
		return dateRangeLabels.findAndGet(l => l.id === this.dateValue, l => l.showWeekStart, false)
	}

	private get dateClauseType (): DateClauseType | null {
		return this.selectedDateClauseType
	}

	private get shouldBeDynamic (): boolean {
		return this.selectedDateClauseType !== DateClauseType.ExactDate || this.operator === Operator.DateRange
	}

	private set dateClauseType (value: DateClauseType | null) {
		if (this.selectedDateClauseType === null) {
			this.selectedDateClauseType = value
		} else {
			this.selectedDateClauseType = value

			var selectedDateRange = this.selectedDateRange(value!)
			if (selectedDateRange) {
				this.valueChanged(selectedDateRange)
			} else {
				this.valueChanged(null)
			}

			this.$emit("dateClauseTypeChanged", value)
		}
	}

	public get validWeekNumberSelected (): boolean {
		return !!this.selectedSearchClauseParameters.weekNumber &&
			this.selectedSearchClauseParameters.weekNumber <= 52 &&
			this.selectedSearchClauseParameters.weekNumber >= 1
	}

	private selectedDateRange (value: DateClauseType): string {
		var selectedDateRange = ""

		switch (this.selectedDateClauseType) {
			case DateClauseType.CurrentWeek:
				selectedDateRange = DateRangeOptions.CurrentWeek
				break
			case DateClauseType.CurrentMonth:
				selectedDateRange = DateRangeOptions.CurrentMonth
				break
			case DateClauseType.CurrentQuarter:
				selectedDateRange = DateRangeOptions.CurrentQuarter
				break
			case DateClauseType.YearToDate:
				selectedDateRange = DateRangeOptions.YearToDate
				break
			case DateClauseType.WeekNumber:
				selectedDateRange = DateRangeOptions.WeekNumber
				break
			case DateClauseType.MonthRange:
				selectedDateRange = DateRangeOptions.MonthRange
				break
			case DateClauseType.DateRange:
				selectedDateRange = DateRangeOptions.DateRange
				break
			case DateClauseType.PreviousWeek:
				selectedDateRange = DateRangeOptions.PreviousWeek
				break
			case DateClauseType.NextWeek:
				selectedDateRange = DateRangeOptions.NextWeek
				break
		}
		return selectedDateRange
	}

	mounted () {
		this.dateClauseType = this.getDateClauseType()
		this.assignSearchClauseParameters(this.selectedSearchClauseParameters)
	}

	@Watch("selectedSearchClauseParameters")
	ValuePropChanged (newParameters: SearchClauseParameters) {
		this.assignSearchClauseParameters(newParameters)
	}

	assignSearchClauseParameters (newParameters: SearchClauseParameters) {
		if (!newParameters) {
			return
		}

		this.selectedDayOfWeek = this.DayOfWeek[newParameters.startDayOfWeek]

		this.selectedWeekNumber = newParameters.weekNumber ? newParameters.weekNumber : null
		this.startDate = newParameters.startDate ? newParameters.startDate : null
		this.endDate = newParameters.endDate ? newParameters.endDate : null
		this.startMonthRange = newParameters.startMonthRange ? newParameters.startMonthRange : null
		this.endMonthRange = newParameters.endMonthRange ? newParameters.endMonthRange : null
		this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
	}

	getDateClauseType (): DateClauseType | null {
		if (!this.value) {
			return DateClauseType.RelativeCurrentDate
		}

		if (this.dynamicValue) {
			if (this.operator !== Operator.DateRange) {
				if (this.dateValue && dateRangeLabels.map(c => c.id).includes(this.dateValue)) {
					return DateClauseType.RelativeDateRange
				} else {
					return DateClauseType.RelativeCurrentDate
				}
			}

			switch (this.value.value) {
				case DateRangeOptions.CurrentWeek:
					return DateClauseType.CurrentWeek
				case DateRangeOptions.CurrentMonth:
					return DateClauseType.CurrentMonth
				case DateRangeOptions.CurrentQuarter:
					return DateClauseType.CurrentQuarter
				case DateRangeOptions.YearToDate:
					return DateClauseType.YearToDate
				case DateRangeOptions.WeekNumber:
					return DateClauseType.WeekNumber
				case DateRangeOptions.DateRange:
					return DateClauseType.DateRange
				case DateRangeOptions.MonthRange:
					return DateClauseType.MonthRange
				case DateRangeOptions.PreviousWeek:
					return (this.getIsFeatureEnabled(TenantFeatures.PreviousWeekFilter))
						? DateClauseType.PreviousWeek : null
				case DateRangeOptions.NextWeek:
					return (this.getIsFeatureEnabled(TenantFeatures.NextWeekFilter))
						? DateClauseType.NextWeek : null
			}
		}
		return DateClauseType.ExactDate
	}

	startDayOfWeekChanged (newStartDayOfWeekLabel: string) {
		this.$set(this.selectedSearchClauseParameters, "startDayOfWeek", (DayOfWeek as any)[newStartDayOfWeekLabel])
		this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
		this.selectedDayOfWeek = newStartDayOfWeekLabel
	}

	startMonthChanged (newStartMonth: Date) {
		var startMonth = moment(newStartMonth).utc().toDate()
		this.$set(this.selectedSearchClauseParameters, "startMonthRange", startMonth)
		this.startMonthRange = startMonth

		// Set endMonth to the start date
		if (this.endMonthRange! < startMonth) {
			var endMonth = moment(startMonth).add("month", 1).subtract("millisecond", 1).toDate()
			this.$set(this.selectedSearchClauseParameters, "endMonthRange", endMonth)
			this.endMonthRange = endMonth
		}

		this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
	}

	endMonthChanged (newEndMonth: Date) {
		var endMonth = moment(newEndMonth).subtract("millisecond", 1).utc().toDate()
		this.$set(this.selectedSearchClauseParameters, "endMonthRange", endMonth)
		this.endMonthRange = endMonth

		this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
	}

	valueChanged (newValue: any) {
		var updatedValue = cloneDeep(this.value) || {
			value: null,
			dynamic: this.shouldBeDynamic
		}

		if (updatedValue.value !== newValue) {
			updatedValue.dynamic = this.shouldBeDynamic
			updatedValue.value = newValue

			this.$emit("input", updatedValue)
		}
	}

	dateValueChanged (newValue: Date) {
		if (!newValue) {
			this.valueChanged(null)
			return
		}

		this.valueChanged(newValue)
	}

	dateClauseChanged (newValue: DateClauseType) {
		if (newValue === DateClauseType.ExactDate) {
			this.dynamicValueChanged(false)
		} else {
			this.dynamicValueChanged(true)
		}
	}

	numberValueChanged (newValue: number) {
		let updatedValue = cloneDeep(this.value)

		if (!updatedValue) {
			updatedValue = {
				value: null,
				dynamic: this.selectedDateClauseType !== DateClauseType.ExactDate
			}
		}

		if (updatedValue.value !== newValue) {
			updatedValue.value = newValue
			this.$emit("input", updatedValue)
		}
	}

	dynamicValueChanged (newValue: boolean) {
		var updatedValue = cloneDeep(this.value) || { value: null, dynamic: false }

		if (updatedValue.dynamic !== newValue) {
			updatedValue.dynamic = newValue
			this.$emit("input", updatedValue)
		}
	}

	dateRangeDateValueChanged (newValue: Date | null, dateRangeType: string) {
		if (dateRangeType === "startDate") {
			this.$set(this.selectedSearchClauseParameters, "startDate", newValue)
			this.startDate = newValue

			if (this.endDate && this.startDate && this.startDate > this.endDate) {
				let newEndDate = new Date(this.startDate)
				newEndDate.setHours(23, 59, 59, 999)

				this.$set(this.selectedSearchClauseParameters, "endDate", newEndDate)

				this.endDate = newEndDate
			}
		} else if (dateRangeType === "endDate") {
			this.$set(this.selectedSearchClauseParameters, "endDate", newValue)
			this.endDate = newValue

			if (this.startDate && this.endDate && this.startDate > this.endDate) {
				let newStartDate = new Date(this.endDate)
				newStartDate.setHours(0, 0, 0, 0)

				this.$set(this.selectedSearchClauseParameters, "startDate", newStartDate)

				this.startDate = newStartDate
			}
		}

		this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
	}

	weekNumberChanged (newWeekNumber: number) {
		// validation
		if (!newWeekNumber || newWeekNumber > 52 || newWeekNumber < 1) {
			this.$set(this.selectedSearchClauseParameters, "weekNumber", null)
			this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
		} else {
			this.$set(this.selectedSearchClauseParameters, "weekNumber", newWeekNumber)
			this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
		}
		this.selectedWeekNumber = newWeekNumber
	}

	clearWeekNumber () {
		this.selectedWeekNumber = null
		this.$set(this.selectedSearchClauseParameters, "weekNumber", null)
		this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
	}

	clearMonthRangeSelection () {
		this.startMonthRange = null
		this.endMonthRange = null
		this.$set(this.selectedSearchClauseParameters, "startMonthRange", null)
		this.$set(this.selectedSearchClauseParameters, "endMonthRange", null)
		this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
	}
}

